import React, { createContext, useContext, useEffect, useState } from "react";
import axiosInstance from "@/API/axiosInstance";

const AuthContext = createContext(null);

const apiError = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.detail ||
  e?.response?.data?.error ||
  e?.message ||
  "Something went wrong";

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = logged in
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;

    const token = localStorage.getItem("authToken");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }

    axiosInstance
      .get("/myprofile")
      .then(({ data }) => {
        if (!done) setUser(data);
      })
      .catch(() => {
        if (!done) setUser(false);
      })
      .finally(() => {
        if (!done) setReady(true);
      });

    return () => {
      done = true;
    };
  }, []);

  /**
   * ✅ NEW: Used for flows where backend already returns { token, user }
   * Example: POST /api/v1/events/session/:sessionId/join-guest
   *
   * - Saves token to localStorage using the SAME key your app uses: "authToken"
   * - Updates in-memory auth state immediately so pages like Lobby see `user` right away
   */
  const applyAuth = (token, userObj) => {
    if (token) localStorage.setItem("authToken", token);
    setUser(userObj || false);
    setReady(true);
  };

  // STEP 1: /login -> sends OTP
  const requestOtp = async (phoneNumber) => {
    try {
      const { data } = await axiosInstance.post("/login", { phoneNumber });
      if (!data?.status) {
        return {
          ok: false,
          error: data?.message || "Failed to send OTP",
          data,
        };
      }
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  // STEP 2: /verifyotp -> returns user + token
  const confirmOtp = async (phoneNumber, otp) => {
    try {
      const { data } = await axiosInstance.post("/verifyotp", {
        phoneNumber,
        otp,
      });

      if (!data?.status) {
        return {
          ok: false,
          error: data?.message || "OTP verification failed",
          data,
        };
      }

      const token = data?.data?.token;
      if (token) localStorage.setItem("authToken", token);

      // verify response already contains the user object in data.data
      setUser(data?.data || false);

      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const guestLogin = async () => {
    try {
      const { data } = await axiosInstance.post("/guest-login");

      if (!data?.status) {
        return {
          ok: false,
          error: data?.message || "Guest login failed",
          data,
        };
      }

      const token = data?.data?.token;
      if (token) localStorage.setItem("authToken", token);

      // guest response has user nested at data.data.user
      const u = data?.data?.user;

      // keep extra flags if you want them available in `user` state
      const mergedUser = u
        ? {
            ...u,
            requiresRegistration: data?.data?.requiresRegistration,
            requiresApproval: data?.data?.requiresApproval,
            isGuest: data?.data?.isGuest ?? u?.isGuest,
          }
        : data?.data || false;

      setUser(mergedUser);

      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const register = async (name, email, password, extras = {}) => {
    try {
      const { data } = await axiosInstance.post("/register", {
        name,
        email,
        password,
        ...extras,
      });

      if (!data?.status) {
        return {
          ok: false,
          error: data?.message || "Registration failed",
          data,
        };
      }

      const token = data?.data?.token;
      if (token) localStorage.setItem("authToken", token);

      setUser(data?.data || false);

      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post("/logout");
    } catch {}
    localStorage.removeItem("authToken");
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await axiosInstance.get("/myprofile");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  };

  // country codes list (for your Login screen dropdown)
  const getCountryCodes = async () => {
    const { data } = await axiosInstance.get("/countries");
    return data?.data ?? data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        requestOtp,
        confirmOtp,
        guestLogin,
        applyAuth, // ✅ NEW
        logout,
        refresh,
        refreshUser: refresh,
        getCountryCodes,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
